/**
 * Derivasi warna & parameter untuk shader liquid-glass dari konfigurasi orb
 * yang SUDAH ADA — bukan skema warna baru. Dua stop admin (colorFrom/colorTo,
 * atau hasil resolveOrbColors bila followTheme menyala) tetap satu-satunya
 * input warna; fungsi di sini hanya menurunkan 4 titik palet yang dikirim ke
 * shader sebagai uniform, dan memetakan slot parameter generik (lihat
 * ORB_VARIANT_SPECS) ke satuan yang dipakai GLSL.
 *
 * Semuanya fungsi murni tanpa DOM/GPU — diuji langsung dengan `bun:test`,
 * sama seperti orb-colors.ts dan orb-motion.ts.
 */

import {
	hexToRgb,
	oklchToRgb,
	type Rgb,
	rgbToOklch,
	shortestHueDelta,
} from "@/components/assistant/orb/orb-colors";
import { paramOr } from "@/components/assistant/orb/orb-frame";

/** Warna netral dipakai bila colorFrom/colorTo rusak — orb tetap tampil, tidak melempar. */
const FALLBACK_RGB: Rgb = { r: 136, g: 136, b: 136 };

/**
 * Selisih hue yang mengitari sisi PANJANG lingkaran warna, kebalikan dari
 * `shortestHueDelta` yang dipakai gradien Canvas 2D. Liquid-glass sengaja
 * menyapu rona sebanyak mungkin antara dua warna admin supaya terasa
 * "berwarna-warni" seperti referensi, bukan gradien dua-warna yang datar.
 * Bila kedua warna punya hue yang sama persis, hasilnya satu putaran penuh
 * (360°) — tetap menyapu rona penuh, bukan diam di satu warna.
 */
export function longWayHueDelta(shortDelta: number): number {
	if (shortDelta === 0) return Math.PI * 2;
	return shortDelta > 0 ? shortDelta - Math.PI * 2 : shortDelta + Math.PI * 2;
}

/**
 * 4 titik warna siklis untuk shader liquid-glass, diturunkan dari colorFrom →
 * colorTo lewat sisi panjang roda hue (bukan skema warna terpisah). Dipakai
 * sebagai uniform `uColor0..uColor3`; shader mencampur di antaranya mengikuti
 * pola aliran permukaan (fbm domain-warped), bukan fresnel.
 */
export function deriveLiquidGlassStops(
	colorFrom: string,
	colorTo: string,
): readonly [Rgb, Rgb, Rgb, Rgb] {
	const fromRgb = hexToRgb(colorFrom) ?? FALLBACK_RGB;
	const toRgb = hexToRgb(colorTo) ?? FALLBACK_RGB;
	const from = rgbToOklch(fromRgb);
	const to = rgbToOklch(toRgb);
	const delta = longWayHueDelta(shortestHueDelta(from.h, to.h));

	const stops: Rgb[] = [];
	for (let i = 0; i < 4; i++) {
		const t = i / 4; // 0, 0.25, 0.5, 0.75 — stop ke-4 (t=1) tersirat sama dengan stop ke-0 saat shader membungkus siklusnya
		const l = from.l + (to.l - from.l) * t;
		// Kroma dijaga tetap hidup di tengah siklus, bukan jatuh linear ke nol,
		// supaya warna "antara" tidak terlihat pudar dibanding kedua ujungnya.
		const c = Math.max(from.c, to.c) * (0.85 + 0.15 * Math.sin(t * Math.PI));
		stops.push(oklchToRgb({ l, c, h: from.h + delta * t }));
	}
	return stops as [Rgb, Rgb, Rgb, Rgb];
}

/** Komponen 0–255 → pecahan 0–1 yang dipakai uniform `vec3` GLSL. */
export function rgbToUnit({ r, g, b }: Rgb): readonly [number, number, number] {
	return [r / 255, g / 255, b / 255];
}

/** Parameter shader liquid-glass dalam satuan GLSL, sudah dijepit ke rentang aman. */
export interface LiquidGlassUniforms {
	/** Kekuatan lipatan permukaan, 0–1. */
	distortion: number;
	/** Kecepatan animasi, 0–1.5. */
	speed: number;
	/** Skala pola warna mengalir, 1–4 (kecil = pita lebar). */
	flow: number;
	/** Kekuatan 3 titik kilau, 0–2. */
	sparkle: number;
}

/**
 * Slot parameter generik (lihat ORB_VARIANT_SPECS) → satuan GLSL. Rentang
 * integer di DB sengaja 100× lebih besar dari satuan shader (mis. distortion
 * 0–100 → uDistortion 0–1) supaya kolom param tetap Int tanpa migrasi — lihat
 * komentar di ORB_VARIANT_SPECS untuk kedua varian liquid-glass.
 */
export function toLiquidGlassUniforms(
	params: Readonly<Record<string, number>>,
): LiquidGlassUniforms {
	return {
		distortion: paramOr(params, "distortion", 40) / 100,
		speed: paramOr(params, "speed", 85) / 100,
		flow: paramOr(params, "flow", 200) / 100,
		sparkle: paramOr(params, "sparkle", 90) / 100,
	};
}
